import { useTranslate } from '@tolgee/react';
import { Store } from 'components/StoreScopes';
import { useCallback } from 'react';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { stores2storeScopes } from 'views/BootGuide/views/AiModel/share';

export interface StoreGroupWithScopes extends Knowledge.StoreGroup {
  storeScopes: General.StoreScope[];
}

export function storeIds2stores<T extends Store>(
  storeIds: number[],
  storeMap: Partial<Record<number, T>>,
) {
  return storeIds.reduce<T[]>((res, id) => {
    if (storeMap[id]) {
      res.push(storeMap[id]);
    }
    return res;
  }, []);
}
export function storeGroup2withScopes(
  storeGroup: Knowledge.StoreGroup,
  storeMap: Partial<Record<number, Store>>,
): StoreGroupWithScopes {
  return {
    ...storeGroup,
    storeScopes: stores2storeScopes(storeIds2stores(storeGroup.storeIds, storeMap)),
  };
}
export function storeGroupList2withScopes(
  storeGroupList: Knowledge.StoreGroup[],
  storeMap: Partial<Record<number, Store>>,
) {
  return storeGroupList.map((storeGroup) => storeGroup2withScopes(storeGroup, storeMap));
}

export function storeScopes2storeIds(storeScopes: General.StoreScope[]) {
  return storeScopes.reduce<number[]>((res, { regionStoreScopes }) => {
    return regionStoreScopes.reduce((res, { storeIds }) => {
      if (storeIds) {
        res.push(...storeIds);
      }
      return res;
    }, res);
  }, []);
}

export function useGetStoreGroupName() {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const getStoreGroupName = useCallback(
    ({ isDefault, name }: Knowledge.StoreGroup) => {
      if (isDefault) {
        return t('默认组');
      }
      const getRegion = (regionName: string) => {
        return {
          阿根廷: 'ar',
          巴西: 'br',
          智利: 'cl',
          哥伦比亚: 'co',
          西班牙: 'es',
          法国: 'fr',
          英国: 'gb',
          印度尼西亚: 'id',
          墨西哥: 'mx',
          马来西亚: 'my',
          菲律宾: 'ph',
          波兰: 'pl',
          新加坡: 'sg',
          泰国: 'th',
          台湾虾皮: 'tw',
          美国: 'us',
          越南: 'vn',
          荷兰: 'nl',
          比利时: 'be',
          捷克: 'cz',
          奥地利: 'at',
          希腊: 'gr',
          葡萄牙: 'pt',
          匈牙利: 'hu',
        }[regionName];
      };
      if (name.endsWith('本土组')) {
        const [regionName] = name.split('本土组');
        const region = getRegion(regionName);
        if (region) {
          return t('x本土组', { x: getRegionLabel(region) });
        }
      } else if (name.endsWith('跨境组')) {
        const [regionName] = name.split('跨境组');
        const region = getRegion(regionName);
        if (region) {
          return t('x跨境组', { x: getRegionLabel(region) });
        }
      } else if (name.endsWith('3PF组')) {
        const [regionName] = name.split('3PF组');
        const region = getRegion(regionName);
        if (region) {
          return t('x3PF组', { x: getRegionLabel(region) });
        }
      }
      return name;
    },
    [t, getRegionLabel],
  );

  return {
    t,
    getStoreGroupName,
  };
}
